import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { WorkingHoursModalComponent } from '../working-hours-modal/working-hours-modal.component';
import { ModalController } from '@ionic/angular';
import { PreorderType, PreorderTypeArray } from '../../../smoothr-web-app-core/enums/PreorderType';
import moment, { Moment } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { isVenueOpen } from '../../../smoothr-web-app-core/utils/utils';
import { TimeUtils } from 'src/smoothr-web-app-core/utils/time-utils';
import { RepositoryService } from 'src/smoothr-web-app-core/services/repository/repository.service';
import Address from 'src/smoothr-web-app-core/models/Address';
import { calculateGeoDistance } from 'src/maps/utils/maps-leaflet';
import { AppComponent } from 'src/app/app.component';

@Component({
	selector: 'app-venue-suggestion',
	templateUrl: './venue-suggestion.component.html',
	styleUrls: ['venue-suggestion.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueSuggestionComponent implements OnInit {
	constructor(
		private modalCtrl: ModalController,
		private repository: RepositoryService,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {}

	get venue(): Venue {
		return this._venue;
	}

	@Input()
	set venue(venue: Venue) {
		if (venue && venue.name.includes('Losteria bonn joseph-beuys-allee')) {
			console.log(JSON.parse(JSON.stringify(venue, null, 2)));
		}
		// Only update if the venue reference has actually changed
		if (this._venue === venue) {
			return;
		}

		this._venue = venue;
		this.acceptsDelivery = false;
		this.acceptsTakeaway = false;
		this.acceptsInside = false;
		this.firstDeliveryTime = null;
		this.firstTakeAwayTime = null;
		this.firstInsideTime = null;
		this.isVenueOpen = false;
		this.externalDelivery = [];
		this.distance = 0;
		this.showOpenInfo = null;
		this.reopenTime = null;

		if (venue === undefined || venue === null) {
			this.cdr.markForCheck();
			return;
		}

		// Set initial isVenueOpen value, will be updated below with special hours consideration
		this.isVenueOpen = isVenueOpen(venue);

		let isOpenResturant = TimeUtils.doesHoursMatchNow(venue.openingHours);
		let isDeliveryEnabled = this.venue.openingHours.length > 0 && TimeUtils.doesHoursMatchNow(this.venue.deliveryHours);
		this.closedBySpecialHours = false;
		this.closedBySpecialDeliveryHours = false;
		try {
			const now = moment();

			// Handle restaurant special opening hours
			if (this.venue?.specialOpeningHours?.length > 0) {
				const foundSpecialDate = this.venue.specialOpeningHours.find(it => moment(it.date).isSame(now, 'date'));
				if (foundSpecialDate) {
					if (foundSpecialDate.isClosed) {
						if (foundSpecialDate.openedAt && foundSpecialDate.closedAt) {
							const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);
							if (now.isBetween(specialHours.openedAt, specialHours.closedAt)) {
								isOpenResturant = false;
								this.closedBySpecialHours = true;
								this.isVenueOpen = false;
								
								// Calculate next available opening time
								// Check if there are regular hours today after the closure period ends
								const closureEndTime = specialHours.closedAt;
								const todaySchedule = TimeUtils.getSchedulesOfDay(now, this.venue.openingHours);
								let foundNextOpenToday = false;
								
								for (const hours of todaySchedule) {
									const schedule = TimeUtils.hoursToSchedule(hours);
									if (!schedule) continue;

									// Case 1: The special closure ends DURING this regular schedule (Resuming operations)
									// e.g. Closed 10:00-15:00, Regular 07:00-23:59 -> Reopens at 15:00
									if (closureEndTime.isSameOrAfter(schedule.openedAt) && closureEndTime.isBefore(schedule.closedAt)) {
										this.reopenTime = closureEndTime.format('HH:mm');
										foundNextOpenToday = true;
										break;
									}

									// Case 2: The schedule starts AFTER the closure ends (Next shift)
									// e.g. Closed 10:00-15:00, Regular 16:00-22:00 -> Opens at 16:00
									if (schedule.openedAt.isAfter(closureEndTime)) {
										this.reopenTime = schedule.openedAt.format('HH:mm');
										foundNextOpenToday = true;
										break;
									}
								}
								
								// If no more hours today or closure is near end of day (23:00+), show tomorrow's opening
								if (!foundNextOpenToday || closureEndTime.hour() >= 23) {
									const tomorrow = now.clone().add(1, 'days');
									const tomorrowSchedule = TimeUtils.getSchedulesOfDay(tomorrow, this.venue.openingHours);
									if (tomorrowSchedule.length > 0) {
										// Sort to get earliest opening tomorrow
										const sortedTomorrow = tomorrowSchedule.sort((a, b) => 
											a.openedAt.localeCompare(b.openedAt)
										);
										this.reopenTime = sortedTomorrow[0].openedAt;
									} else {
										this.reopenTime = closureEndTime.format('HH:mm');
									}
								}
							}
						} else {
							isOpenResturant = false;
							this.closedBySpecialHours = true;
							this.isVenueOpen = false;
						}
					} else {
						// Special opening hours for today - apply to restaurant
						const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);
						const isWithinSpecialHours = now.isBetween(specialHours.openedAt, specialHours.closedAt);
						isOpenResturant = isWithinSpecialHours;
						this.isVenueOpen = isOpenResturant;
					}
				}
			}

			// Handle delivery special opening hours separately
			if (this.venue?.specialDeliveryHours?.length > 0) {
				const foundSpecialDeliveryDate = this.venue.specialDeliveryHours.find(it => moment(it.date).isSame(now, 'date'));

				if (foundSpecialDeliveryDate) {
					if (foundSpecialDeliveryDate.isClosed && foundSpecialDeliveryDate.openedAt && foundSpecialDeliveryDate.closedAt) {
						// Special delivery break period - exclude time slots during these hours
						const specialDeliveryHours = TimeUtils.transformSpecialHours(foundSpecialDeliveryDate);
						const isWithinDeliveryBreak = now.isBetween(specialDeliveryHours.openedAt, specialDeliveryHours.closedAt);

						if (isWithinDeliveryBreak) {
							isDeliveryEnabled = false;
							this.closedBySpecialDeliveryHours = true;
						}
						// If not within break period, use the regular delivery hours logic
					} else if (foundSpecialDeliveryDate.isClosed) {
						// Special delivery closure for today - disable delivery for entire day
						isDeliveryEnabled = false;
						this.closedBySpecialDeliveryHours = true;
					} else {
						// Special delivery opening hours for today (isClosed: false)
						const specialDeliveryHours = TimeUtils.transformSpecialHours(foundSpecialDeliveryDate);
						const isWithinSpecialDeliveryHours = now.isBetween(specialDeliveryHours.openedAt, specialDeliveryHours.closedAt);

						// Only enable delivery if within special delivery hours AND delivery is configured
						if (this.venue.deliveryHours && this.venue.deliveryHours.length > 0) {
							isDeliveryEnabled = isWithinSpecialDeliveryHours;
						} else {
							// No regular delivery hours configured, so disable delivery
							isDeliveryEnabled = false;
						}
					}
				}
			} else if (this.venue?.specialOpeningHours?.length > 0) {
				// Fallback: If no special delivery hours exist, use special opening hours for delivery
				const foundSpecialDate = this.venue.specialOpeningHours.find(it => moment(it.date).isSame(now, 'date'));
				if (foundSpecialDate && !foundSpecialDate.isClosed) {
					const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);
					const isWithinSpecialHours = now.isBetween(specialHours.openedAt, specialHours.closedAt);

					if (this.venue.deliveryHours && this.venue.deliveryHours.length > 0) {
						isDeliveryEnabled = isWithinSpecialHours;
					} else {
						isDeliveryEnabled = false;
					}
				}
			}
		} catch (e) {}

		if (this.venue?.openingHours?.length > 0) {
			if (this.isVenueOpen) {
				// Check if there are special opening hours for today that should override the regular closing time
				const now = moment();
				let closingTimeToShow = this.venue.openingHours[moment().day()]?.closedAt;
				
				if (this.venue?.specialOpeningHours?.length > 0) {
					const foundSpecialDate = this.venue.specialOpeningHours.find(it => moment(it.date).isSame(now, 'date'));
					if (foundSpecialDate && !foundSpecialDate.isClosed && foundSpecialDate.closedAt) {
						// Use special hours closing time when venue is open with special hours
						// Transform the special hours to get properly formatted time
						const specialHours = TimeUtils.transformSpecialHours(foundSpecialDate);
						closingTimeToShow = specialHours.closedAt.format('HH:mm');
					}
				}
				
				this.checkOpenAtTime(closingTimeToShow, 'venue_suggestion.closed_at');
			} else if (this.reopenTime) {
				this.checkOpenAtTime(this.reopenTime, 'venue_suggestion.open_at');
			} else if (!this.closedBySpecialHours) {
				this.checkOpenAtTime(this.venue.openingHours[moment().day()]?.openedAt, 'venue_suggestion.open_at');
			}
		}

		if (this.showHiddenVenue) {
			this.acceptsTakeaway = this.venue.preorderTakeAway && this.venue.isServiceActivated;

			this.acceptsInside = this.venue.preorderIn && this.venue.isServiceActivated;
			this.acceptsDelivery =
				isDeliveryEnabled && this.venue.deliveryEnabled && this.venue.isServiceActivated && this.venue.isPublished;
		} else {
			this.acceptsTakeaway =
				isOpenResturant && this.venue.preorderTakeAway && this.venue.isServiceActivated && this.venue.isPublished;

			this.acceptsInside = isOpenResturant && this.venue.preorderIn && this.venue.isServiceActivated && this.venue.isPublished;
			this.acceptsDelivery =
				isDeliveryEnabled && this.venue.deliveryEnabled && this.venue.isServiceActivated && this.venue.isPublished;
		}

		try {
			const externalServices = (venue?.externalDeliveryService ?? []) as any[];
			if (externalServices.length > 0) {
				// Show external delivery partners if venue is open and published, regardless of venue's own delivery service
				// Only hide if venue is closed due to special opening hours
				this.externalDelivery = !this.closedBySpecialHours ? externalServices : [];
			}
		} catch (e) {}

		this.cdr.markForCheck();
	}
	environment = environment;
	@Input() type: PreorderType;
	@Output() venuePick = new EventEmitter<{ venue: Venue; type: PreorderType }>();
	@Input() showHiddenVenue: boolean = false;
	isVenueOpen = false;
	pt = PreorderType;
	@Input()
	isMap = false;
	@Input()
	overridePostalDelivery = false;
	acceptsDelivery = false;
	firstDeliveryTime: string = null;
	acceptsInside = false;
	firstInsideTime: string = null;
	acceptsTakeaway = false;
	firstTakeAwayTime: string = null;
	@Input()
	selected = false;
	ptArray = PreorderTypeArray.filter(it => it !== PreorderType.PARK_COLLECT && it !== PreorderType.INSIDE);
	openSelectionType = AppComponent.largeScreen ? true : false;
	externalDelivery: any[] = [];
	distance = 0;
	showOpenInfo: string;
	private _venue: Venue;
	closedBySpecialHours = false;
	closedBySpecialDeliveryHours = false;
	reopenTime: string;

	ngOnInit(): void {
		if (this.repository._address && this.venue) {
			const distanceValue = this.calcDistanse(this.repository._address, this.venue);
			if (distanceValue) {
				this.distance = Number(distanceValue.toFixed(2));
				this.cdr.markForCheck();
			}
		}
	}
	ionViewDidEnter() {}

	calcDistanse(address: Address, venue: Venue) {
		return calculateGeoDistance(address.lat, address.lng, venue.location.coordinates[1], venue.location.coordinates[0]);
	}

	selectVenue(venue: Venue, type: PreorderType) {
		console.log('SELECT VENUE');
		if (!this.accepts(type)) {
			return;
		}
		this.venuePick.emit({ venue, type });
	}

	accepts(type: PreorderType): boolean {
		switch (type) {
			case PreorderType.DELIVERY:
				return this.acceptsDelivery;
			case PreorderType.TAKE_AWAY:
				return this.acceptsTakeaway;
			case PreorderType.INSIDE:
				return this.acceptsInside;
			default:
				return false;
		}
	}

	openModal(venue: Venue) {
		WorkingHoursModalComponent.show(this.modalCtrl, venue, this.type);
	}

	timeToText(venue: Venue, time: Moment | null) {
		if (time && venue.slot) {
			const now = moment().add(
				Math.abs((moment().minutes() % venue.slot.interval) - venue.slot.interval) % venue.slot.interval,
				'minutes'
			);
			const roundedTime = time.add(
				Math.abs((time.minutes() % venue.slot.interval) - venue.slot.interval) % venue.slot.interval,
				'minutes'
			);
			const relevantTime = now.isAfter(roundedTime) ? now : roundedTime;
			const isToday = now.dayOfYear() === relevantTime.dayOfYear() && now.year() === relevantTime.year();
			return (isToday ? '' : this.translate.instant('venue_suggestion.tomorrow') + ' ') + relevantTime.format('HH:mm');
		} else {
			return null;
		}
	}
	isVenueOpenFunc() {
		return this.isVenueOpen ? this.translate.instant('venue_suggestion.open') : this.translate.instant('venue_suggestion.close');
	}
	checkIfExist(item: string) {
		return ((this.venue.externalDeliveryService as any[]) ?? []).find(it => it?.name === item)?.link;
	}
	navigateTo(link: string) {
		if (!link) {
			return;
		}
		window.open(link, '_blank');
	}
	checkIfHaveExternalServices(value: any) {
		if (value) {
			if (value?.length > 0) {
				// Show external delivery partners if venue is open and has external links
				// Don't hide based on venue's own delivery service availability
				return value.find(it => !!it.link) && !this.closedBySpecialHours;
			}
		}
		return false;
	}
	checkOpenAtTime(hour: string, translationValue: string) {
		try {
			if (!hour || this.showOpenInfo) {
				return;
			}

			this.showOpenInfo =
				this.translate.instant(translationValue + '.from') + ' ' + hour + this.translate.instant(translationValue + '.time');
		} catch (e) {}
	}
}
